/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { MouseEventHandler, ReactNode } from 'react';

// @mui material components
import Collapse from '@mui/material/Collapse';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Icon from '@mui/material/Icon';
import Tooltip from '@mui/material/Tooltip';
import { Theme } from '@mui/material/styles';

// Material Dashboard 2 PRO React TS components
import MDBox from 'components/MDBox';

// Custom styles for the SidenavItem
import { item, itemContent, itemArrow } from 'examples/Sidenav/styles/sidenavItem';

// Material Dashboard 2 PRO React TS contexts
import { useMaterialUIController } from 'context';

// Declaring props types for SidenavCollapse
interface Props {
    color?: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error' | 'light' | 'dark';
    name: string;
    active?: boolean | string;
    nested?: boolean;
    children?: ReactNode;
    open?: boolean;

    /// Contador ao lado do nome. Vem de fora porque o menu não deve saber de
    /// onde o número sai — hoje é agendamento, amanhã pode ser outra coisa.
    badge?: number;
    /// O que o contador conta, ao passar o mouse.
    badgeHint?: string;
    icon?: ReactNode;
    onClick?: MouseEventHandler<HTMLLIElement>;
}

function SidenavItem({
    color,
    name,
    active,
    nested,
    children,
    icon,
    open,
    badge,
    badgeHint,
    ...rest
}: Props): JSX.Element {
    const [controller] = useMaterialUIController();
    const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;

    return (
        <>
            <ListItem
                {...rest}
                component="li"
                sx={(theme) =>
                    item(theme, { active, color, transparentSidenav, whiteSidenav, darkMode })
                }
            >
                <MDBox
                    sx={(theme) =>
                        itemContent(theme, {
                            icon,
                            active,
                            miniSidenav,
                            name,
                            open,
                            nested,
                            transparentSidenav,
                            whiteSidenav,
                            darkMode,
                        })
                    }
                >
                    <Icon
                        component="i"
                        sx={(theme) =>
                            itemArrow(theme, {
                                open,
                                miniSidenav,
                                transparentSidenav,
                                whiteSidenav,
                                darkMode,
                                active
                            })
                        }
                    >
                        {icon}
                    </Icon>
                    <ListItemText primary={name} />

                    {/* Zero não vira bolinha: contador que nunca some deixa de
                        ser lido depois do segundo dia. */}
                    {!miniSidenav && Boolean(badge) && (
                        <Tooltip title={badgeHint ?? ''} placement="right">
                            <MDBox
                                sx={{
                                    minWidth: 20,
                                    height: 20,
                                    px: 0.75,
                                    borderRadius: '10px',
                                    bgcolor: 'error.main',
                                    color: '#fff',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    lineHeight: '20px',
                                    textAlign: 'center',
                                }}
                            >
                                {badge}
                            </MDBox>
                        </Tooltip>
                    )}
                    {children && (
                        <Icon
                            component="i"
                            sx={(theme) =>
                                itemArrow(theme, {
                                    open,
                                    miniSidenav,
                                    transparentSidenav,
                                    whiteSidenav,
                                    darkMode,
                                })
                            }
                        >
                            expand_less
                        </Icon>
                    )}
                </MDBox>
            </ListItem>
            {children && (
                <Collapse in={open} timeout="auto" unmountOnExit {...rest}>
                    {children}
                </Collapse>
            )}
        </>
    );
}

// Declaring default props for SidenavItem
SidenavItem.defaultProps = {
    color: 'info',
    active: false,
    nested: false,
    children: false,
    open: false,
};

export default SidenavItem;
